/**
 * @file Controlled Drugs admin submit owner.
 */

import {
    addDoc,
    collection,
    doc,
    serverTimestamp,
    updateDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { appData, state } from "../../app/state/state.js";
import { saveDrugRoleSettingsCallable } from "../../app/firebase/callables.js";
import {
    getCollectionName,
} from "../../shared/utils/itemLookup.js";

const drugRoleFormBaselines = new WeakMap();

/**
 * Captures the displayed role values independently of the live app cache.
 * @param {HTMLFormElement} form Role edit form.
 * @param {object|null} role Role snapshot used to open the form.
 * @returns {void}
 */
export function captureDrugRoleFormBaseline(form, role) {
    drugRoleFormBaselines.set(form, role?.id ? {
        id: role.id,
        name: role.name ?? null,
        description: role.description ?? null,
        permissions: structuredClone(role.permissions ?? null),
        rank: role.rank ?? null,
    } : null);
}

function getNextOrder(items, field = "order") {
    const maxOrder = (items || []).reduce((max, item) => {
        const value = Number(item?.[field]);
        return Number.isFinite(value) ? Math.max(max, value) : max;
    }, 0);
    return maxOrder + 1;
}

function applyDrugInventoryFormData({ data, form, id }) {
    if (form.elements.isActive) {
        data.isActive = form.elements.isActive.checked;
    }

    data.strength = Number(data.strength);
    data.packSize = Number(data.packSize);
    if (data.order) {data.order = Number(data.order);}
    data.minStockThreshold = {};

    const setThreshold = (locationId, thresholdType, value) => {
        if (value === "" || value === null) {return;}
        if (!data.minStockThreshold[locationId]) {
            data.minStockThreshold[locationId] = {};
        }
        data.minStockThreshold[locationId][thresholdType] = Number(value);
    };

    Object.keys(data).forEach((key) => {
        if (!key.startsWith("threshold_")) {return;}
        if (key.endsWith("_reminder") || key.endsWith("_warning")) {
            const typePart = key.endsWith("_reminder") ? "reminder" : "warning";
            const locationId = key.substring(
                "threshold_".length,
                key.length - typePart.length - 1,
            );
            setThreshold(locationId, typePart, data[key]);
        }
        delete data[key];
    });

    if (!id) {
        data.order = getNextOrder(appData.drugInventory || []);
        data.stockLevels = { main: 0, total: 0 };
        data.forecast = {};
        data.isActive = true;
        data.createdBy = state.currentUser.uid;
        data.createdAt = serverTimestamp();
    }
}

function applyDrugRoleFormData(data, id) {
    if (!id) {
        data.rank = getNextOrder(appData.drugRoles || [], "rank");
    }

    data.permissions = {};
    Object.keys(data).forEach((key) => {
        if (!key.startsWith("perm_")) {return;}
        const permissionName = key.replace("perm_", "");
        data.permissions[permissionName] = true;
        delete data[key];
    });
}

/**
 * Saves Controlled Drugs admin inventory or role forms.
 * @param {object} params Submit context.
 * @param {object} params.data Form payload.
 * @param {HTMLFormElement} params.form Source form.
 * @param {string} params.id Existing document ID.
 * @param {'drug_inventory'|'drug_role'} params.type Form type.
 * @returns {Promise<void>}
 */
export async function saveDrugAdminForm({ data, form, id, type }) {
    if (type === "drug_inventory") {
        applyDrugInventoryFormData({ data, form, id });
    } else if (type === "drug_role") {
        applyDrugRoleFormData(data, id);
    } else {
        throw new Error(`Unsupported drug admin form type: ${type}`);
    }

    if (type === "drug_role") {
        const source = drugRoleFormBaselines.get(form);
        if (id && source?.id !== id) {
            throw new Error("Reopen the drug role before saving.");
        }
        const expected = id ? Object.fromEntries(Object.keys(data).map((key) =>
            [key, source[key] ?? null])) : {};
        const result = (await saveDrugRoleSettingsCallable({roleId: id || null,
            changes: data, expected})).data;
        if (result?.success !== true) {throw new Error("Drug role save did not complete.");}
        return;
    }

    data.updatedBy = state.currentUser.uid;
    data.updatedAt = serverTimestamp();

    const collectionName = getCollectionName(type);
    if (id) {
        await updateDoc(doc(db, collectionName, id), data);
    } else {
        await addDoc(collection(db, collectionName), data);
    }
}
